<template>
    <div class="table-contact-list-container" :class="{ 'virtual-mode': selectedGroup !== 'none' }">
        <div class="table-contact-list" @scroll="onMainScroll" ref="mainContainer">
            <div class="table-header" v-if="selectedContacts.length === 0">
                <div class="header-content">
                    <div class="title-section">
                        <div class="filter-icon">
                            <i class="material-icons">{{ filterIcon }}</i>
                        </div>
                        <div class="title-text">
                            <span class="filter-name">{{ filterName }}</span>
                        </div>
                    </div>
                    <div class="header-actions">
                        <SupsisButton v-if="filter && !editMode" class="edit-filter-btn" @click="onEditFilter">
                            {{ $t('chat.tableContactList.editFilter') }}
                        </SupsisButton>
                        <div v-if="editMode" class="edit-mode-actions">
                            <SupsisButton @click="onCancelEditFilter" class="cancel-btn">{{ $t('message.cancel') }}</SupsisButton>
                            <SupsisButton primary @click="onSaveFilterChanges" class="save-btn">
                                <i class="material-icons">save</i>
                                {{ $t('common.save') }}
                            </SupsisButton>
                        </div>
                        <div
                            v-if="selectedGroup === 'none' && localContacts.length > 0 && !editMode"
                            class="pagination-controls-compact"
                        >
                            <div class="page-size-selector">
                                <a-select
                                    v-model="cursor.limit"
                                    size="small"
                                    @change="onPageSizeChange"
                                    style="width: 80px"
                                >
                                    <a-select-option :value="10">10</a-select-option>
                                    <a-select-option :value="20">20</a-select-option>
                                    <a-select-option :value="50">50</a-select-option>
                                    <a-select-option :value="100">100</a-select-option>
                                </a-select>
                            </div>
                            <div class="pagination-buttons">
                                <button
                                    :disabled="!cursor.prevToken || loading"
                                    @click="goToPrevPage"
                                    class="pagination-btn-compact"
                                >
                                    <i class="material-icons">chevron_left</i>
                                </button>
                                <button
                                    :disabled="!cursor.nextToken || loading"
                                    @click="goToNextPage"
                                    class="pagination-btn-compact"
                                >
                                    <i class="material-icons">chevron_right</i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FiltersSection
                v-if="editMode"
                :sort-disabled="true"
                :filters="editingFilters"
                :state="state"
                :sort="sortValue"
                @filters-changed="onEditingFiltersChanged"
            />

            <BulkActionsBar
                v-else-if="selectedContacts.length > 0"
                :selected-count="selectedContacts.length"
                :selected-contacts="selectedContactsData"
                @close="clearSelection"
                @select-all="selectAllContacts"
                @deselect-all="clearSelection"
                @bulk-action-complete="onBulkActionComplete"
            />

            <div v-else class="table-filter-section">
                <div class="filters-section">
                    <div class="state-and-group-selectors">
                        <div class="state-selector">
                            <a-tooltip
                                placement="top"
                                :title="`${$t('chat.tableContactList.chatStatus')}: ${getStatusTitle(effectiveState)}`"
                                :mouse-enter-delay="0.4"
                            >
                                <MinimalPopoverSelect
                                    :value="effectiveState"
                                    :options="stateOptions"
                                    class="state-selector"
                                    :icon="'layers'"
                                    :show-dropdown-arrow="false"
                                    button-class="chip"
                                    @change="onStateChange"
                                />
                            </a-tooltip>
                        </div>

                        <div class="group-selector">
                            <a-tooltip placement="top" :title="$t('chat.tableContactList.grouping')" :mouse-enter-delay="0.4">
                                <MinimalPopoverSelect
                                    :value="selectedGroup"
                                    :options="groupOptions"
                                    class="group-selector"
                                    :icon="'group_work'"
                                    :show-dropdown-arrow="false"
                                    button-class="chip"
                                    @change="onGroupChange"
                                    :filter-name="$t('chat.tableContactList.grouping')"
                                />
                            </a-tooltip>
                        </div>
                    </div>
                </div>
            </div>

            <div class="contact-list-view">
                <ContactList
                    ref="contactList"
                    :contacts="allContacts"
                    :loading="loading"
                    :groups="selectedGroup !== 'none' ? groupValues : []"
                    :scroll-loading="selectedGroup !== 'none'"
                    :table-local-storage-key="`contact-table-${filterId || 'default'}`"
                    :scroll-top="scrollTop"
                    :container-height="containerHeight"
                    :selectable="true"
                    :max-select-count="500"
                    :filterable="true"
                    :filters="tableFilters"
                    :sort-field="sortField"
                    :sort-direction="sortDirection"
                    @scroll-load="onScrollLoad"
                    @selection-change="onSelectionChange"
                    @filter-toggle="onFilterToggle"
                    @sort-change="onSortChange"
                />
            </div>
        </div>

        <FilterNameModal
            :visible="showFilterNameModal"
            :filter-name="pendingFilterName"
            :suggested-name="pendingSuggestedName"
            @confirm="confirmFilterName"
            @cancel="cancelFilterName"
            @update-suggested-name="updateSuggestedName"
        />
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import MinimalPopoverSelect from "./MinimalPopoverSelect.vue";
import ContactList from "./ContactList.vue";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import BulkActionsBar from "./BulkActionsBar.vue";
import FiltersSection from "./FiltersSection.vue";
import FilterNameModal from "./FilterNameModal.vue";
import _ from "lodash";
import { systemMessageStream, SystemMessageTypes } from "@/utils/systemPubSub";
import { updateFilter, createFilter } from "@/api/favFilters";

export default {
    name: "TableContactList",
    props: {
        filterId: {
            type: String,
            default: null,
        },
    },
    data: function () {
        return {
            selectedGroup: "none",
            groupValues: [],
            loading: false,
            localContacts: [],
            contactsCache: {},
            groupMetadata: {},
            systemMessageSubscription: null,
            scrollTop: 0,
            containerHeight: 0,
            selectedContacts: [],
            selectedContactsData: [],
            editMode: false,
            editingFilters: [],
            cursor: {
                limit: 20,
                nextToken: null,
                prevToken: null,
                firstPageToken: null,
            },
            totalContacts: 0,
            previousView: null,
            showFilterNameModal: false,
            pendingFilterName: "",
            pendingSuggestedName: "",
            pendingFilterIcon: "",
            pendingFilterVisibility: "private",
            sortValue: "relevance",
            sortField: null,
            sortDirection: null,
        };
    },
    mounted() {
        this.systemMessageSubscription = systemMessageStream.subscribe((message) => {
            if (message.type === SystemMessageTypes.TABLE_CONTACT_ACTION) {
                this.handleContactAction(message.payload);
            }
        });

        this.$nextTick(() => {
            if (this.$refs.mainContainer) {
                this.containerHeight = this.$refs.mainContainer.clientHeight;
                this.scrollTop = this.$refs.mainContainer.scrollTop || 0;
            }
        });

        if (this.filterId) {
            this.resetState();
            if (this.selectedGroup !== "none") {
                this.generateGroupValues();
            } else {
                this.loadInitialData("onMounted");
            }
        }

        // Ctrl+A ile tümünü seç
        document.addEventListener("keydown", this.handleKeyDown);
    },
    beforeDestroy() {
        if (this.systemMessageSubscription) {
            this.systemMessageSubscription.unsubscribe();
        }
        document.removeEventListener("keydown", this.handleKeyDown);
    },
    components: {
        MinimalPopoverSelect,
        ContactList,
        SupsisButton,
        BulkActionsBar,
        FiltersSection,
        FilterNameModal,
    },
    computed: {
        ...mapState({
            filters: (state) => state.contact.filters,
            tags: (state) => state.tag.tags.data,
            subjects: (state) => state.subject.subjects?.data || [],
            channels: (state) => state.channel.channels || [],
            users: (state) => state.user.users?.data || [],
            state: (state) => state.contact.selectedState,
            activeView: (state) => state.contact.activeView,
        }),
        usersHashData() {
            return _.keyBy(this.users, "_id");
        },
        tableFilters() {
            // editingFilters'ı NewSupsisTable formatına çevir
            return this.transformFiltersToTable(this.editingFilters);
        },
        filter() {
            return this.filters.find((filter) => filter.id === this.filterId);
        },
        isCustomFilter() {
            return this.filter?.type === "custom";
        },
        isSystemFilter() {
            return this.filter?.type === "system";
        },
        filterIcon() {
            return this.filter?.icon || "filter_list";
        },
        filterName() {
            return this.filter?.name || this.$t("chat.tableContactList.allContacts");
        },
        groupOptions() {
            const options = [{ label: this.$t("chat.tableContactList.groupNone"), value: "none", icon: "layers" }];

            const basicGroups = [
                { label: this.$t("chat.tableContactList.groupTag"), value: "tags", icon: "label" },
                { label: this.$t("chat.tableContactList.groupDepartment"), value: "department", icon: "apartment" },
                { label: this.$t("chat.tableContactList.groupChannel"), value: "channel", icon: "alternate_email" },
                { label: this.$t("chat.tableContactList.groupPlatform"), value: "platform", icon: "devices" },
                { label: this.$t("chat.tableContactList.groupOwner"), value: "owner", icon: "person" },
                { label: this.$t("chat.tableContactList.groupUsers"), value: "users", icon: "group" },
                { label: this.$t("chat.tableContactList.groupAnswerStatus"), value: "answerStatus", icon: "question_answer" },
            ];

            options.push(...basicGroups);

            return options;
        },
        hasLastConversationStatusFilter() {
            if (!this.filter || !this.filter.filters) return false;
            return this.filter.filters.some((f) => f.field === "lastConversationStatus" && f.value);
        },
        effectiveState() {
            // lastConversationStatus filtresi varsa her zaman "all" döndür ama store'u değiştirme
            if (this.hasLastConversationStatusFilter) {
                return "all";
            }
            return this.state;
        },
        stateOptions() {
            // lastConversationStatus filtresi varsa sadece "all" seçeneği göster
            if (this.hasLastConversationStatusFilter) {
                return [{ label: this.$t("chat.tableContactList.statusAll"), value: "all", icon: "layers" }];
            }

            return [
                { label: this.$t("chat.tableContactList.statusAll"), value: "all", icon: "layers" },
                { label: this.$t("chat.tableContactList.statusOpen"), value: "open", icon: "lock_open" },
                { label: this.$t("chat.tableContactList.statusClosed"), value: "closed", icon: "lock" },
            ];
        },
        allContacts() {
            if (this.selectedGroup === "none") {
                return this.localContacts;
            }
            const contacts = [];
            const validGroupKeys = this.groupValues.map((g) => g.value);

            for (const groupKey in this.contactsCache) {
                // ✅ Sadece mevcut grup türü için geçerli key'leri kullan
                if (!validGroupKeys.includes(groupKey)) {
                    continue;
                }

                const cachedContacts = this.contactsCache[groupKey] || [];
                for (let i = 0; i < cachedContacts.length; i++) {
                    const contact = Object.assign({}, cachedContacts[i]);
                    contact.__groupKey = groupKey;
                    contact.__groupIndex = i;
                    contacts.push(contact);
                }
            }
            return contacts;
        },
    },
    watch: {
        filter: function (newVal, oldVal) {
            if (!this.filter || oldVal?.id === newVal?.id) return;

            // Component mount edilmemişse duplicate request engellemek için skip
            if (!this.$el) {
                return;
            }

            this.resetState();
            if (this.selectedGroup === "none") {
                this.loadInitialData("watch.filter");
            } else {
                this.generateGroupValues();
            }
        },
        groupValues: function (newVal, oldVal) {
            // ✅ groupValues değiştiğinde cache'deki geçersiz key'leri temizle
            if (this.selectedGroup !== "none") {
                const validKeys = (newVal || []).map((g) => g.value);
                const cacheKeys = Object.keys(this.contactsCache);
                const invalidKeys = cacheKeys.filter((key) => !validKeys.includes(key));

                if (invalidKeys.length > 0) {
                    invalidKeys.forEach((key) => {
                        this.$delete(this.contactsCache, key);
                        this.$delete(this.groupMetadata, key);
                    });
                }
            }
        },
        tags: function () {
            if (this.selectedGroup === "tags" && this.groupValues.length > 0) {
                this.updateGroupLabels();
            }
        },
        subjects: function () {
            if (this.selectedGroup === "department" && this.groupValues.length > 0) {
                this.updateGroupLabels();
            }
        },
        channels: function () {
            if (this.selectedGroup === "channel" && this.groupValues.length > 0) {
                this.updateGroupLabels();
            }
        },
        users: function () {
            if ((this.selectedGroup === "owner" || this.selectedGroup === "users") && this.groupValues.length > 0) {
                this.updateGroupLabels();
            }
        },
        activeView: function (newVal, oldVal) {
            // kanban -> table: listeyi yeniden çek
            if (this.previousView === "kanban" && newVal === "list") {
                this.resetState();
                if (this.selectedGroup !== "none") {
                    this.generateGroupValues();
                } else {
                    this.loadInitialData("activeView.kanban");
                }
            }
            
            // Önceki view'ı sakla
            this.previousView = newVal;
        },
    },
    methods: {
        ...mapActions({
            getFilteredContacts: "contact/getFilteredContacts",
            getContactListGroups: "contact/getContactListGroups",
            updateContactListFilters: "contact/updateContactListFilters",
            setSelectedState: "contact/setSelectedState",
            clearCachedContactsByIds: "contact/clearCachedContactsByIds",
        }),
        createNameKey(name) {
            return name
                .toLowerCase()
                .replace(/ğ/g, "g")
                .replace(/ü/g, "u")
                .replace(/ş/g, "s")
                .replace(/ı/g, "i")
                .replace(/ö/g, "o")
                .replace(/ç/g, "c")
                .replace(/[^a-z0-9\s]/g, "")
                .replace(/^[0-9]+/, "")
                .trim()
                .split(/\s+/)
                .filter(word => word.length > 0)
                .map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
                .join("");
        },
        getUniqueFilterName(baseName) {
            const baseNameKey = this.createNameKey(baseName);
            let uniqueName = baseName;
            let nameKey = baseNameKey;
            
            const allFilters = this.filters || [];
            const existingNameKeys = new Set();
            const existingFilters = [];
            
            allFilters.forEach(filter => {
                let filterNameKey = null;
                if (filter.type === "custom" && filter.key) {
                    filterNameKey = filter.key;
                    existingNameKeys.add(filterNameKey);
                    existingFilters.push({ nameKey: filterNameKey, name: filter.name });
                } else if (filter.type === "system" && filter.name) {
                    filterNameKey = this.createNameKey(filter.name);
                    existingNameKeys.add(filterNameKey);
                    existingFilters.push({ nameKey: filterNameKey, name: filter.name });
                }
            });
            
            if (existingNameKeys.has(nameKey)) {
                const copyLabelKey = this.createNameKey(this.$t("chat.tableContactList.copyLabel"));
                const escapedCopyLabel = copyLabelKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                const baseNameRegex = new RegExp("^" + baseNameKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(" + escapedCopyLabel + "\\d*)?$");

                let maxCloneNumber = 0;
                existingFilters.forEach(filter => {
                    if (filter.nameKey && baseNameRegex.test(filter.nameKey)) {
                        const cloneRegex = new RegExp(escapedCopyLabel + "(\\d+)$");
                        const cloneMatch = filter.nameKey.match(cloneRegex);
                        if (cloneMatch) {
                            const num = parseInt(cloneMatch[1], 10);
                            if (num > maxCloneNumber) {
                                maxCloneNumber = num;
                            }
                        } else if (filter.nameKey === baseNameKey + copyLabelKey) {
                            if (maxCloneNumber === 0) {
                                maxCloneNumber = 1;
                            }
                        }
                    }
                });
                
                const nextCloneNumber = maxCloneNumber + 1;
                const copyLabel = this.$t("chat.tableContactList.copyLabel");
                if (nextCloneNumber === 1) {
                    uniqueName = baseName + " " + copyLabel + " 1";
                } else {
                    uniqueName = baseName + " " + copyLabel + " " + nextCloneNumber;
                }
            }
            
            return uniqueName;
        },
        resetPaginationCursor: function () {
            this.cursor.nextToken = null;
            this.cursor.prevToken = null;
            this.cursor.firstPageToken = null;
        },
        resetState: function () {
            this.contactsCache = {};
            this.groupMetadata = {};
            this.localContacts = [];
            this.resetPaginationCursor();
        },
        getStatusTitle: function (value) {
            if (value === "all") return this.$t("chat.tableContactList.statusAll");
            if (value === "open") return this.$t("chat.tableContactList.statusOpen");
            if (value === "closed") return this.$t("chat.tableContactList.statusClosed");
            return this.$t("chat.tableContactList.statusUnknown");
        },
        onStateChange: function (value) {
            // lastConversationStatus filtresi varsa store'u değiştirme, sadece yeniden yükle
            if (this.hasLastConversationStatusFilter) {
                this.resetState();
                if (this.selectedGroup === "none") {
                    this.loadInitialData("onStateChange");
                } else {
                    this.generateGroupValues();
                }
                return;
            }

            this.setSelectedState(value);
            this.resetState();
            if (this.selectedGroup === "none") {
                this.loadInitialData("onGroupChange");
            } else {
                this.generateGroupValues();
            }
        },
        onGroupChange: function (value) {
            this.selectedGroup = value;
            this.resetState();
            if (value === "none") {
                this.loadInitialData("onGroupChange");
            } else {
                this.generateGroupValues();
            }
        },
        onMainScroll: _.debounce(function (event) {
            const container = event.target;
            this.scrollTop = container.scrollTop;
            this.containerHeight = container.clientHeight;
        }, 50),
        onEditFilter: function () {
            if (this.isCustomFilter) {
                this.editMode = true;
                this.editingFilters = _.cloneDeep(this.filter.filters || []);
                this.selectedGroup = "none";
                this.resetState();
                this.loadEditModeData();
            } else if (this.isSystemFilter) {
                // Sistem filtresi için edit mode'a geç
                this.editMode = true;
                this.editingFilters = _.cloneDeep(this.filter.filters || []);
                this.selectedGroup = "none";
                this.resetState();
                this.loadEditModeData();
            }
        },
        onCancelEditFilter: function () {
            this.editMode = false;
            this.editingFilters = [];
            this.resetState();
            this.loadInitialData("onCancelEditFilter");
        },
        onEditingFiltersChanged: function (payload) {
            this.editingFilters = _.cloneDeep(payload.filters);
            // lastConversationStatus filtresi varsa store'u değiştirme
            if (payload.state && !this.hasLastConversationStatusFilter) {
                this.setSelectedState(payload.state);
            }

            this.resetState();
            this.loadEditModeData();
        },
        transformFiltersToTable: function(filters) {
            // TableContactList formatını (obje) NewSupsisTable formatına (basit) çevir
            return filters.map(function(filter) {
                const transformedFilter = {
                    field: filter.field,
                    value: filter.value,
                    operator: filter.operator
                };
                
                // owner field'ı için: obje → basit string
                if (filter.field === 'owner' && Array.isArray(filter.value)) {
                    transformedFilter.value = filter.value.map(function(val) {
                        if (typeof val === 'object' && val !== null && val.value) {
                            return val.value; // {type: "unowned", value: "unowned"} → "unowned"
                        }
                        return val;
                    });
                }
                // lastConversationUsers field'ı için: obje → basit string
                else if (filter.field === 'lastConversationUsers' && Array.isArray(filter.value)) {
                    transformedFilter.value = filter.value.map(function(val) {
                        if (typeof val === 'object' && val !== null && val.value) {
                            return val.value; // {type: "none", value: "none"} → "none"
                        }
                        return val;
                    });
                }
                
                return transformedFilter;
            });
        },
        loadEditModeData: function () {
            this.loading = true;
            
            const filteredFilters = this.editingFilters.filter((filter) => {
                return filter.value !== null && filter.value !== undefined && filter.value !== "";
            });
            
            const payload = {
                cursor: {
                    limit: 50,
                },
                state: this.effectiveState,
                metadata: {
                    source: "TableContactList.editMode",
                    withoutCount: "true",
                },
                filters: filteredFilters,
            };

            this.getFilteredContacts(payload)
                .then((response) => {
                    this.localContacts = response.contacts || [];
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        onSaveFilterChanges: async function () {
            if (this.isSystemFilter) {
                const proposedName = this.filter?.name || "";
                this.pendingFilterName = proposedName;
                this.pendingFilterIcon = this.filter?.icon || "filter_list";
                this.pendingFilterVisibility = "private";
                if (proposedName) {
                    const uniqueName = this.getUniqueFilterName(proposedName);
                    if (uniqueName !== proposedName) {
                        this.pendingSuggestedName = uniqueName;
                    } else {
                        this.pendingSuggestedName = "";
                    }
                } else {
                    this.pendingSuggestedName = "";
                }
                this.showFilterNameModal = true;
                return;
            } else if (this.isCustomFilter) {
                // Custom filtre için güncelle
                try {
                    await updateFilter(
                        this.filter._id,
                        this.filter.icon,
                        this.filter.name,
                        this.editingFilters,
                        this.filter.visibility
                    );

                    // Vuex state'i güncelle
                    const filters = this.filters.map((f) => {
                        if (f.id === this.filter._id) {
                            f.filters = _.cloneDeep(this.editingFilters);
                        }
                        return f;
                    });
                    this.updateContactListFilters(filters);

                    this.editMode = false;
                    this.editingFilters = [];
                    this.selectedGroup = "none";
                    this.resetState();
                    this.loadInitialData("onSaveFilterChanges");

                    this.$notification.success({
                        message: this.$t("chat.filterPanel.filterUpdatedSuccess"),
                        description: this.$t("chat.filterPanel.filterUpdatedDescription", { name: this.filter.name }),
                    });
                } catch (error) {
                    this.$notification.error({
                        message: this.$t("chat.filterPanel.filterUpdateFailed"),
                        description: error.response?.data?.error || this.$t("chat.filterPanel.filterUpdateError"),
                    });
                }
            }
        },
        createNewFilterFromSystem: async function (filterName) {
            try {
                const createName = filterName.trim();
                const createIcon = this.pendingFilterIcon || this.filter?.icon || "filter_list";
                const userId = _.get(this.$store, "state.auth.user._id");
                
                const response = await createFilter(
                    "page.contact-list",
                    createName,
                    _.cloneDeep(this.editingFilters),
                    userId,
                    null,
                    createIcon,
                    this.pendingFilterVisibility || "private",
                    null
                );

                const filter = response.data || response;
                
                // Yeni filtrenin contact sayısını al
                const payload = {
                    cursor: {
                        limit: 20,
                    },
                    state: this.effectiveState,
                    metadata: {
                        source: "TableContactList.createNewFilterFromSystem",
                    },
                    filters: this.editingFilters.filter((filter) => {
                        return filter.value !== null && filter.value !== undefined && filter.value !== "";
                    }),
                };
                
                const contactsResponse = await this.getFilteredContacts(payload);
                const total = contactsResponse.count || 0;

                // Vuex state'i güncelle
                const filters = [
                    ...this.filters,
                    {
                        type: "custom",
                        id: filter._id,
                        _id: filter._id,
                        name: filter.name,
                        key: filter.nameKey,
                        icon: filter.icon,
                        filters: _.cloneDeep(this.editingFilters),
                        count: total,
                        visibility: filter.visibility,
                    },
                ];
                this.updateContactListFilters(filters);

                this.editMode = false;
                this.editingFilters = [];
                this.selectedGroup = "none";
                this.resetState();

                // Yeni filtreye geçiş yap
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        inbox: filter.nameKey,
                    },
                });

                if (filter.name !== createName) {
                    this.$notification.success({
                        message: this.$t("chat.tableContactList.filterCreatedSuccess"),
                        description: this.$t("chat.tableContactList.filterCreatedNameDuplicate", {
                            createName: createName,
                            filterName: filter.name,
                        }),
                    });
                } else {
                    this.$notification.success({
                        message: this.$t("chat.tableContactList.filterCreatedSuccess"),
                        description: this.$t("chat.filterPanel.filterCreatedDescription", { filterName: filter.name }),
                    });
                }
            } catch (error) {
                const errorMessage = error.response?.data?.error || error.message || this.$t("chat.tableContactList.filterCreateError");
                this.$notification.error({
                    message: this.$t("chat.tableContactList.filterCreateFailed"),
                    description: errorMessage,
                });
            }
        },
        confirmFilterName: async function (filterName) {
            this.showFilterNameModal = false;
            await this.createNewFilterFromSystem(filterName);
            this.pendingFilterName = "";
            this.pendingSuggestedName = "";
        },
        cancelFilterName: function () {
            this.showFilterNameModal = false;
            this.pendingFilterName = "";
            this.pendingSuggestedName = "";
            this.editMode = false;
            this.editingFilters = [];
            this.resetState();
            this.loadInitialData("cancelFilterName");
        },
        updateSuggestedName: function (filterName) {
            if (filterName && filterName.trim()) {
                const uniqueName = this.getUniqueFilterName(filterName.trim());
                if (uniqueName !== filterName.trim()) {
                    this.pendingSuggestedName = uniqueName;
                } else {
                    this.pendingSuggestedName = "";
                }
            } else {
                this.pendingSuggestedName = "";
            }
        },
        onSelectionChange: function (payload) {
            this.selectedContacts = payload.selectedRowKeys || [];
            this.selectedContactsData = payload.selectedData || [];
        },
        clearSelection: function () {
            this.selectedContacts = [];
            this.selectedContactsData = [];
            if (this.$refs.contactList && this.$refs.contactList.$children) {
                const supsisTable = this.$refs.contactList.$children.find(
                    (child) => child.$options.name === "NewSupsisTable"
                );
                if (supsisTable && supsisTable.clearSelection) {
                    supsisTable.clearSelection();
                }
            }
        },
        onBulkActionComplete: function () {
            if (this.selectedContacts && this.selectedContacts.length > 0) {
                const contactIds = this.selectedContacts.map((id) => (typeof id === "object" && id && id._id ? id._id : id));
                this.clearCachedContactsByIds(contactIds);
            }

            // Seçimi koruyarak sadece listeyi yenile
            const preservedSelection = [...this.selectedContacts];
            const preservedSelectionData = [...this.selectedContactsData];

            if (this.selectedGroup === "none") {
                this.loadInitialData("onBulkActionComplete");
            } else {
                this.generateGroupValues();
            }

            // Seçimi geri yükle
            this.$nextTick(() => {
                this.selectedContacts = preservedSelection;
                this.selectedContactsData = preservedSelectionData;
            });
        },
        generateGroupValues: async function () {
            if (this.selectedGroup === "none") {
                this.groupValues = [];
                return;
            }

            try {
                const response = await this.getContactListGroups({
                    groupField: this.selectedGroup,
                    statusState: this.effectiveState,
                });

                const groups = response.groups || [];
                this.groupValues = groups.map((group) => {
                    const groupId = group._id;
                    let label = groupId;

                    if (this.selectedGroup === "tags") {
                        const tag = this.tags.find((t) => t._id === groupId);
                        label = tag?.name || groupId;
                    } else if (this.selectedGroup === "department") {
                        const subject = this.subjects.find((s) => s._id === groupId);
                        if (subject) {
                            label =
                                subject.languageContent[this.$i18n.locale]?.name ||
                                subject.languageContent[Object.keys(subject.languageContent)[0]]?.name ||
                                groupId;
                        }
                    } else if (this.selectedGroup === "channel") {
                        const channel = this.channels.find((c) => c._id === groupId);
                        label = channel?.name || groupId;
                    } else if (this.selectedGroup === "platform") {
                        const platformNames = {
                            supsis: this.$t("chat.tableContactList.platformSupsis"),
                            email: this.$t("chat.tableContactList.platformEmail"),
                            whatsapp: this.$t("chat.tableContactList.platformWhatsapp"),
                            web: this.$t("chat.tableContactList.platformWeb"),
                            instagram: this.$t("chat.tableContactList.platformInstagram"),
                            telegram: this.$t("chat.tableContactList.platformTelegram"),
                            messenger: this.$t("chat.tableContactList.platformMessenger"),
                            "meta-leads": this.$t("chat.tableContactList.platformMetaLeads"),
                            trendyol: this.$t("chat.tableContactList.platformTrendyol"),
                            linkedin: this.$t("chat.tableContactList.platformLinkedin"),
                        };
                        label = platformNames[groupId] || groupId;
                    } else if (this.selectedGroup === "owner" || this.selectedGroup === "users") {
                        const user = this.usersHashData[groupId];
                        label = user?.name || user?.email || groupId;
                    } else if (this.selectedGroup === "answerStatus") {
                        const statusNames = {
                            answered: this.$t("chat.tableContactList.answerStatusAnswered"),
                            unanswered: this.$t("chat.tableContactList.answerStatusUnanswered"),
                        };
                        label = statusNames[groupId] || groupId;
                    }

                    return {
                        label: label,
                        value: groupId,
                        count: group.count || 0,
                    };
                });
            } catch (error) {
                this.groupValues = [];
            }
        },
        loadInitialData: function (source) {
            if (this.selectedGroup === "none") {
                this.loading = true;

                const payload = {
                    cursor: {
                        limit: this.cursor.limit,
                    },
                    state: this.effectiveState,
                    metadata: {
                        source: "TableContactList.loadInitialData." + source,
                    },
                };

                // Sort bilgisini ekle
                if (this.sortField && this.sortDirection) {
                    payload.sort = {
                        field: this.sortField,
                        direction: this.sortDirection,
                    };
                }

                // Add pagination tokens if available
                if (this.cursor.nextToken) {
                    payload.cursor.nextToken = this.cursor.nextToken;
                }
                if (this.cursor.prevToken) {
                    payload.cursor.prevToken = this.cursor.prevToken;
                }
                if (this.cursor.firstPageToken) {
                    payload.cursor.firstPageToken = this.cursor.firstPageToken;
                }

                this.getFilteredContacts(payload)
                    .then((response) => {
                        this.localContacts = response.contacts || [];

                        // Update pagination cursors
                        this.cursor.nextToken = response.cursor?.nextToken || null;
                        this.cursor.prevToken = response.cursor?.prevToken || null;
                        this.cursor.firstPageToken = response.cursor?.firstPageToken || this.cursor.firstPageToken;

                        // Update total count if available
                        if (response.count !== undefined) {
                            this.totalContacts = response.count;
                        }

                        this.loading = false;
                    })
                    .catch((error) => {
                        this.loading = false;
                    });
            }
        },
        onScrollLoad: function (payload) {
            const { groupKey, offset } = payload;

            // Grup değişikliği kontrolü - eski grup key'i için istek atmayalım
            const isValidGroupKey = this.groupValues.some((g) => g.value === groupKey);
            if (!isValidGroupKey) {
                return;
            }

            if (!this.contactsCache[groupKey]) {
                this.$set(this.contactsCache, groupKey, []);
            }

            if (!this.groupMetadata[groupKey]) {
                this.$set(this.groupMetadata, groupKey, {
                    nextToken: null,
                    loadedCount: 0,
                    isLoading: false,
                });
            }

            const cachedContacts = this.contactsCache[groupKey];
            const metadata = this.groupMetadata[groupKey];

            if (offset < cachedContacts.length) {
                return;
            }

            if (metadata.isLoading) {
                return;
            }

            if (metadata.loadedCount > 0 && !metadata.nextToken) {
                return;
            }
            this.$set(this.groupMetadata[groupKey], "isLoading", true);

            const requestPayload = {
                cursor: {
                    limit: 50,
                },
                state: this.effectiveState,
                metadata: {
                    source: "TableContactList.onScrollLoad",
                    withoutCount: "true",
                },
                group: {
                    field: this.selectedGroup,
                    value: groupKey,
                },
            };

            if (metadata.nextToken) {
                requestPayload.cursor.nextToken = metadata.nextToken;
            }

            this.getFilteredContacts(requestPayload)
                .then((response) => {
                    const contacts = response.contacts || [];
                    const newNextToken = response.cursor?.nextToken || null;
                    const updatedCache = cachedContacts.concat(contacts);
                    this.$set(this.contactsCache, groupKey, updatedCache);

                    this.$set(this.groupMetadata[groupKey], "loadedCount", updatedCache.length);
                    this.$set(this.groupMetadata[groupKey], "nextToken", newNextToken);
                    this.$set(this.groupMetadata[groupKey], "isLoading", false);
                })
                .catch((error) => {
                    this.$set(this.groupMetadata[groupKey], "isLoading", false);
                });
        },
        handleContactAction: function (payload) {
            // Edit mode'dayken reactive güncellemeleri durdur
            if (this.editMode) {
                return;
            }

            const { action, data } = payload;

            if (action === "add_contact") {
                this.addContactToList(data);
            } else if (action === "update_contact") {
                this.updateContactInList(data);
            } else if (action === "remove_contact") {
                this.removeContactFromList(data.contactId);
            } else if (action === "update_contact_fields") {
                this.updateContactFieldsInList(data.contactId, data.set);
            }
        },
        addContactToList: function (contact) {
            if (this.selectedGroup === "none") {
                const exists = this.localContacts.find((c) => c._id === contact._id);
                if (!exists) {
                    this.localContacts.unshift(contact);
                }
            } else {
                const groupKey = this.getContactGroupKey(contact);
                if (groupKey && this.groupedContacts[groupKey]) {
                    const exists = this.groupedContacts[groupKey].find((c) => c._id === contact._id);
                    if (!exists) {
                        this.groupedContacts[groupKey].unshift(contact);
                    }
                }
            }
        },
        updateContactInList: function (contact) {
            if (this.selectedGroup === "none") {
                const index = this.localContacts.findIndex((c) => c._id === contact._id);
                if (index !== -1) {
                    this.$set(this.localContacts, index, contact);
                }
            } else {
                const groupKey = this.getContactGroupKey(contact);
                if (groupKey && this.groupedContacts[groupKey]) {
                    const index = this.groupedContacts[groupKey].findIndex((c) => c._id === contact._id);
                    if (index !== -1) {
                        this.$set(this.groupedContacts[groupKey], index, contact);
                    }
                }
            }
        },
        removeContactFromList: function (contactId) {
            if (this.selectedGroup === "none") {
                const index = this.localContacts.findIndex((c) => c._id === contactId);
                if (index !== -1) {
                    this.localContacts.splice(index, 1);
                }
            } else {
                for (const groupKey in this.groupedContacts) {
                    const index = this.groupedContacts[groupKey].findIndex((c) => c._id === contactId);
                    if (index !== -1) {
                        this.groupedContacts[groupKey].splice(index, 1);
                        break;
                    }
                }
            }
        },
        updateContactFieldsInList: function (contactId, fields) {
            if (this.selectedGroup === "none") {
                const contact = this.localContacts.find((c) => c._id === contactId);
                if (contact) {
                    Object.assign(contact, fields);
                }
            } else {
                for (const groupKey in this.groupedContacts) {
                    const contact = this.groupedContacts[groupKey].find((c) => c._id === contactId);
                    if (contact) {
                        Object.assign(contact, fields);
                        break;
                    }
                }
            }
        },
        getContactGroupKey: function (contact) {
            if (this.selectedGroup === "tags") {
                return contact.tags;
            } else if (this.selectedGroup === "department") {
                return contact.relatedDepartments?.[0];
            } else if (this.selectedGroup === "channel") {
                return contact.channel;
            } else if (this.selectedGroup === "platform") {
                return contact.platform;
            } else if (this.selectedGroup === "owner") {
                return contact.owner;
            } else if (this.selectedGroup === "users") {
                return contact.lastConversationUsers?.[0];
            } else if (this.selectedGroup === "answerStatus") {
                return contact.answerStatus;
            }
            return null;
        },
        updateGroupLabels: function () {
            this.groupValues = this.groupValues.map((group) => {
                const groupId = group.value;
                let label = groupId;

                if (this.selectedGroup === "tags") {
                    const tag = this.tags.find((t) => t._id === groupId);
                    label = tag?.name || groupId;
                } else if (this.selectedGroup === "department") {
                    const subject = this.subjects.find((s) => s._id === groupId);
                    if (subject) {
                        label =
                            subject.languageContent[this.$i18n.locale]?.name ||
                            subject.languageContent[Object.keys(subject.languageContent)[0]]?.name ||
                            groupId;
                    }
                } else if (this.selectedGroup === "channel") {
                    const channel = this.channels.find((c) => c._id === groupId);
                    label = channel?.name || groupId;
                } else if (this.selectedGroup === "owner" || this.selectedGroup === "users") {
                    const user = this.usersHashData[groupId];
                    label = user?.name || user?.email || groupId;
                }

                return {
                    ...group,
                    label: label,
                };
            });
        },
        goToNextPage: function () {
            if (!this.cursor.nextToken || this.loading) {
                return;
            }
            this.cursor.prevToken = null;
            this.loadInitialData("goToNextPage");
        },
        goToPrevPage: function () {
            if (!this.cursor.prevToken || this.loading) {
                return;
            }
            this.cursor.nextToken = null;
            this.loadInitialData("goToPrevPage");
        },
        onPageSizeChange: function () {
            this.resetPaginationCursor();
            this.localContacts = [];
            this.loadInitialData("onPageSizeChange");
        },
        selectAllContacts: function () {
            const allContactIds = this.localContacts.map((c) => c._id);
            this.selectedContacts = allContactIds;
            this.selectedContactsData = [...this.localContacts];

            if (this.$refs.contactList && this.$refs.contactList.$children) {
                const supsisTable = this.$refs.contactList.$children.find(
                    (child) => child.$options.name === "NewSupsisTable"
                );
                if (supsisTable && supsisTable.selectAll) {
                    supsisTable.selectAll();
                }
            }
        },
        handleKeyDown: function (event) {
            // Ctrl+A (Windows/Linux) veya Cmd+A (Mac)
            if ((event.ctrlKey || event.metaKey) && event.key === "a") {
                // Sadece seçim modunda iken çalışsın
                if (this.selectedContacts.length > 0) {
                    event.preventDefault();
                    this.selectAllContacts();
                }
            }
        },
        onFilterToggle: function (payload) {
            // Edit mode'a geç
            if (!this.editMode) {
                this.editMode = true;
                this.editingFilters = _.cloneDeep(this.filter?.filters || []);
                this.selectedGroup = "none";
                this.resetState();
            }
            
            const isMultiple = payload.isMultiple || false;
            
            // Basit formatı (NewSupsisTable'dan gelen) obje formatına çevir
            const transformToObjFormat = function(val, field) {
                // owner field'ı için
                if (field === 'owner') {
                    if (val === 'unowned' || val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
                        return { type: 'unowned', value: 'unowned' };
                    }
                    return { type: 'agent', value: val };
                }
                // lastConversationUsers field'ı için
                if (field === 'lastConversationUsers') {
                    if (val === 'none' || val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
                        return { type: 'none', value: 'none' };
                    }
                    return { type: 'agent', value: val };
                }
                // Diğer fieldlar için olduğu gibi dön
                return val;
            };
            
            // Obje karşılaştırma
            const isSameValue = function(v1, v2) {
                if (typeof v1 === 'object' && v1 !== null && typeof v2 === 'object' && v2 !== null) {
                    return v1.value === v2.value;
                }
                return v1 === v2;
            };
            
            // ACTION: REMOVE - Filtreyi veya array'den elemanı çıkar
            if (payload.action === 'remove') {
                // Mevcut filtreyi bul
                const existingFilter = this.editingFilters.find(f => f.field === payload.field);
                
                if (existingFilter) {
                    // ✅ MULTIPLE: Array'den eleman çıkar
                    if (isMultiple && Array.isArray(existingFilter.value)) {
                        // payload.value'yu obje formatına çevir
                        let valuesToRemove = Array.isArray(payload.value) ? payload.value : [payload.value];
                        valuesToRemove = valuesToRemove.map(function(val) {
                            return transformToObjFormat(val, payload.field);
                        });
                        
                        const newArray = existingFilter.value.filter(function(v) {
                            return !valuesToRemove.some(function(vr) {
                                return isSameValue(v, vr);
                            });
                        });
                        
                        if (newArray.length === 0) {
                            // Array boşaldıysa filtreyi tamamen kaldır
                            this.editingFilters = this.editingFilters.filter(f => f.field !== payload.field);
                        } else {
                            // Array'i güncelle
                            existingFilter.value = newArray;
                        }
                    } else {
                        // Non-multiple ise filtreyi tamamen kaldır
                        this.editingFilters = this.editingFilters.filter(f => f.field !== payload.field);
                    }
                }
            }
            // ACTION: ADD - Filtre ekle veya güncelle
            else if (payload.action === 'add') {
                // Mevcut filtreyi bul
                const existingFilter = this.editingFilters.find(f => f.field === payload.field);
                
                // Yeni filtre value'sunu hazırla
                let newFilterValue = payload.value;
                
                // ✅ MULTIPLE: Array mantığını burada işle
                if (isMultiple) {
                    // payload.value array ise elemanlarını aç, değilse array'e çevir
                    let valuesToAdd = Array.isArray(payload.value) ? payload.value : [payload.value];
                    
                    // Değerleri obje formatına çevir
                    valuesToAdd = valuesToAdd.map(function(val) {
                        return transformToObjFormat(val, payload.field);
                    });
                    
                    if (existingFilter && Array.isArray(existingFilter.value)) {
                        // Sadece mevcut array'de olmayan değerleri ekle
                        const newValues = valuesToAdd.filter(function(v) {
                            return !existingFilter.value.some(function(ev) {
                                return isSameValue(v, ev);
                            });
                        });
                        if (newValues.length > 0) {
                            newFilterValue = existingFilter.value.concat(newValues);
                        } else {
                            // Zaten var, işlem yapma
                            return;
                        }
                    } else if (existingFilter && !Array.isArray(existingFilter.value)) {
                        // Array değilse, array'e çevir ve yeni değerleri ekle
                        newFilterValue = [existingFilter.value].concat(valuesToAdd);
                    } else {
                        // Yeni array oluştur
                        newFilterValue = [].concat(valuesToAdd);
                    }
                } else {
                    // Non-multiple ise obje formatına çevir
                    newFilterValue = transformToObjFormat(payload.value, payload.field);
                }
                
                // Yeni filtre objesi oluştur
                const newFilter = {
                    field: payload.field,
                    value: newFilterValue
                };
                
                // Operator varsa ekle
                if (payload.operator) {
                    newFilter.operator = payload.operator;
                }
                
                if (existingFilter) {
                    // Mevcut filtreyi güncelle
                    existingFilter.value = newFilterValue;
                    if (payload.operator) {
                        existingFilter.operator = payload.operator;
                    }
                } else {
                    // Yeni filtre ekle
                    this.editingFilters.push(newFilter);
                }
            }
            // Verileri yeniden yükle
            this.loadEditModeData();
        },
        onSortChange: function (sortPayload) {
            // sortPayload: { key: 'columnKey', order: 'asc' | 'desc' | null }
            if (!sortPayload.key || !sortPayload.order) {
                // Sort kaldırıldı
                this.sortField = null;
                this.sortDirection = null;
                this.sortValue = "relevance";
            } else {
                // Sort uygulandı
                this.sortField = sortPayload.key;
                this.sortDirection = sortPayload.order.toUpperCase(); // 'asc' -> 'ASC', 'desc' -> 'DESC'
                this.sortValue = `${sortPayload.key}_${this.sortDirection}`;
            }
            
            // Verileri yeniden yükle
            this.resetPaginationCursor();
            if (this.selectedGroup === "none") {
                this.loadInitialData("onSortChange");
            } else {
                this.generateGroupValues();
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.table-contact-list-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;

    &:not(.virtual-mode) {
        .table-contact-list {
            overflow: auto;
            display: flex;
            flex-direction: column;

            .contact-list-view {
                overflow: hidden;
                display: flex;
                flex-direction: column;
            }
        }
    }
}

.table-contact-list {
    width: 100%;
    flex: 1;
    padding: 24px;
    overflow: auto;

    // Header Section
    .table-header {
        margin-bottom: 20px;
        flex-shrink: 0;
    }

    .header-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }

    .title-section {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-left: auto;
    }

    .filter-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: #f0f0f0;
        border-radius: 8px;
        transition: all 0.2s ease;

        .material-icons {
            font-size: 20px;
            color: #22272d;
        }
    }

    .title-text {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .filter-name {
        font-size: 18px;
        font-weight: 600;
        color: #22272d;
    }

    .edit-filter-btn {
        height: 32px;
        padding: 0 12px;
        font-size: 13px;
        font-weight: 500;
    }

    .edit-mode-actions {
        display: flex;
        align-items: center;
        gap: 8px;

        .cancel-btn,
        .save-btn {
            height: 32px;
            padding: 0 12px;
            font-size: 13px;
            font-weight: 500;
        }

        .save-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;

            .material-icons {
                font-size: 16px;
            }
        }
    }

    .table-filter-section {
        display: flex;
        flex-direction: column;
        gap: 16px;
        flex-shrink: 0;
    }

    .filters-section {
        margin-top: 0;
        margin-bottom: 15px;
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    .state-and-group-selectors {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .state-selector,
    .group-selector {
        .chip {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            padding: 0.35rem 0.7rem;
            border: 1px solid #c7d0d7;
            border-radius: 999px;
            background: #fff;
            color: #22272d;
            cursor: pointer;
            user-select: none;
            transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
            font-weight: 600;

            &:hover {
                background: #f0f0f0;
                color: #22272d;

                .material-icons {
                    color: #22272d !important;
                }
            }

            .material-icons {
                font-size: 18px;
                line-height: 1;
                color: #22272d;
            }
        }
    }

    .contact-list-view {
        flex: 1;
    }

    // Compact Pagination Controls (in header)
    .pagination-controls-compact {
        display: flex;
        align-items: center;
        gap: 8px;

        .page-size-selector {
            display: flex;
            align-items: center;
        }

        .pagination-buttons {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .pagination-btn-compact {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: #fff;
            border: 1px solid #d0d7de;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;

            .material-icons {
                font-size: 18px;
                color: #586069;
            }

            &:hover:not(:disabled) {
                background: #f6f8fa;
                border-color: #8c959f;

                .material-icons {
                    color: #24292f;
                }
            }

            &:disabled {
                opacity: 0.4;
                cursor: not-allowed;
            }
        }
    }
}

</style>
